/**
 * ContractorSetupPage.jsx · v2.7.65+E78 · Sensitive Contractor Onboarding
 * ═══════════════════════════════════════════════════════════════════════
 * Private form reached only via a direct URL sent by an admin. Collects
 * the full sensitive-data packet: SSN, DL profile, address, banking,
 * W-9, 3 professional refs, emergency contact, availability, 4 identity
 * documents (client-enhanced via canvas), and a signature-backed
 * background-release PDF assembled client-side with jsPDF.
 *
 * State machine has ONE source of truth (`form` / `docs` / `refs`), and
 * the submit handler runs the full relational-validation gauntlet BEFORE
 * scrolling to the first offending field. The loading overlay only
 * appears during the final network round-trip so the applicant can see
 * validation errors instantly.
 */
import { useCallback, useMemo, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Lock, ShieldCheck, Loader2, ArrowLeft, PenLine, Info } from "lucide-react";
import { Link } from "react-router-dom";
import { jsPDF } from "jspdf";
import SignatureCanvas from "@/components/contractor/SignatureCanvas";
import DocumentSlot   from "@/components/contractor/DocumentSlot";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY","DC",
];
const DAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];
const SLOT4_TYPES = ["Passport","Social Security Card","Military ID","Birth Certificate"];
const FORBIDDEN_RELATIONSHIPS = /family|relative|kin/i;
const E164 = /^\+?[1-9]\d{7,14}$/;                        // ITU E.164
const RFC5322_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;    // pragmatic RFC 5322

// ── Input mask for SSN ────────────────────────────────────────────────
function maskSSN(digitsRaw) {
  const d = (digitsRaw || "").replace(/\D/g, "").slice(0, 9);
  if (d.length <= 3) return d;
  if (d.length <= 5) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
}

const INIT = {
  // Identity
  email: "", first_name: "", middle_name: "", last_name: "",
  driver_phone: "", date_of_birth: "",
  ssn_digits: "",
  // DL
  dl_number: "", dl_expiration_date: "", dl_issuing_state: "OK",
  // Address
  address: "", city: "", state: "OK", zip: "",
  // Banking
  bank_name: "", routing_number: "", account_number: "", account_type: "checking",
  // W-9
  w9_legal_name: "", w9_business_name: "", w9_tax_classification: "individual",
  w9_tin_type: "ssn", w9_tin_number: "",
  // Emergency contact
  ec_name: "", ec_phone: "", ec_relationship: "",
  // Availability
  days_available: [], start_time: "06:00", end_time: "22:00",
  // Background release
  background_release_agreed: false,
  background_release_date: new Date().toISOString().slice(0, 10),
};

const INIT_REFS = [
  { name: "", phone: "", email: "", relationship: "" },
  { name: "", phone: "", email: "", relationship: "" },
  { name: "", phone: "", email: "", relationship: "" },
];

export default function ContractorSetupPage() {
  const [form, setForm] = useState(INIT);
  const [refs, setRefs] = useState(INIT_REFS);
  const [docs, setDocs] = useState({ dl_front: null, dl_back: null, insurance: null, slot4: null });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const sigRef = useRef(null);
  const topAnchorRef = useRef(null);

  const set = (k) => (e) => {
    const v = e?.target?.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
  };
  const setSsn = (e) => {
    const digits = (e.target.value || "").replace(/\D/g, "").slice(0, 9);
    setForm((f) => ({ ...f, ssn_digits: digits }));
  };
  const setRef = (idx, k) => (e) => {
    const v = e.target.value;
    setRefs((rs) => rs.map((r, i) => (i === idx ? { ...r, [k]: v } : r)));
  };
  const toggleDay = (day) => {
    setForm((f) => ({
      ...f,
      days_available: f.days_available.includes(day)
        ? f.days_available.filter((d) => d !== day)
        : [...f.days_available, day],
    }));
  };

  // ── Validation ────────────────────────────────────────────────────
  const validate = useCallback(() => {
    const e = {};

    // Identity
    if (!form.email.trim() || !RFC5322_EMAIL.test(form.email.trim()))
      e.email = "Valid email required";
    if (!form.first_name.trim()) e.first_name = "Required";
    if (!form.last_name.trim())  e.last_name  = "Required";
    const driverDigits = form.driver_phone.replace(/\D/g, "");
    if (driverDigits.length < 10) e.driver_phone = "Valid phone required";
    if (!form.date_of_birth) e.date_of_birth = "Required";

    // SSN
    if (form.ssn_digits.length !== 9) e.ssn = "SSN must be 9 digits";

    // DL
    if (!form.dl_number.trim())          e.dl_number = "Required";
    if (!form.dl_expiration_date)        e.dl_expiration_date = "Required";
    if (!/^[A-Z]{2}$/.test(form.dl_issuing_state)) e.dl_issuing_state = "Pick a state";

    // Address
    if (!form.address.trim()) e.address = "Required";
    if (!form.city.trim())    e.city    = "Required";
    if (!/^[A-Z]{2}$/.test(form.state)) e.state = "Pick a state";
    if (!/^\d{5}(-\d{4})?$/.test(form.zip.trim())) e.zip = "Invalid ZIP";

    // Docs (dl_front, dl_back, insurance mandatory; slot4 must have doc_type if present)
    if (!docs.dl_front)   e.dl_front  = "Front of DL required";
    if (!docs.dl_back)    e.dl_back   = "Back of DL required";
    if (!docs.insurance)  e.insurance = "Proof of insurance required";
    if (docs.slot4 && !docs.slot4.doc_type) e.slot4 = "Select a document type before upload";

    // References — 3 distinct, no family
    refs.forEach((r, i) => {
      const rel = (r.relationship || "").trim();
      if (!r.name.trim())         e[`ref_${i}_name`]  = "Required";
      if (!rel)                   e[`ref_${i}_rel`]   = "Required";
      else if (FORBIDDEN_RELATIONSHIPS.test(rel))
                                  e[`ref_${i}_rel`]   = "Cannot be family or relative";
      const rDigits = r.phone.replace(/\D/g, "");
      const rPhone  = r.phone.startsWith("+") ? r.phone : `+${rDigits}`;
      if (!E164.test(rPhone.replace(/[^\d+]/g, ""))) e[`ref_${i}_phone`] = "Invalid phone (E.164)";
      if (!r.email.trim() || !RFC5322_EMAIL.test(r.email.trim()))
                                  e[`ref_${i}_email`] = "Invalid email";
    });
    const emails = refs.map((r) => r.email.trim().toLowerCase()).filter(Boolean);
    if (new Set(emails).size !== emails.length) {
      e.refs_unique = "Reference emails must all be distinct";
    }

    // Emergency contact
    if (!form.ec_name.trim())      e.ec_name = "Required";
    if (!form.ec_relationship.trim()) e.ec_relationship = "Required";
    const ecDigits = form.ec_phone.replace(/\D/g, "");
    if (ecDigits.length < 10)      e.ec_phone = "Valid phone required";
    else if (ecDigits === driverDigits)
                                   e.ec_phone = "Must differ from your own phone";

    // Availability
    if (form.days_available.length === 0) e.days_available = "Select at least one day";
    if (form.end_time <= form.start_time) e.end_time = "End time must be after start time";

    // Banking
    if (!form.bank_name.trim())                       e.bank_name = "Required";
    if (!/^\d{9}$/.test(form.routing_number.replace(/\D/g, "")))
                                                      e.routing_number = "Routing must be 9 digits";
    if (form.account_number.replace(/\D/g, "").length < 4)
                                                      e.account_number = "Account number too short";

    // W-9
    if (!form.w9_legal_name.trim())                   e.w9_legal_name = "Required";
    if (form.w9_tin_number.replace(/\D/g, "").length < 9)
                                                      e.w9_tin_number = "TIN must be 9+ digits";

    // Background release
    if (!form.background_release_agreed)              e.background_release_agreed = "You must agree";
    if (!sigRef.current?.isSigned())                  e.signature = "Signature required";

    setErrors(e);
    return e;
  }, [form, refs, docs]);

  // ── Compile signature PDF client-side (jsPDF) ────────────────────
  const compileSignaturePDF = useCallback(async () => {
    const pdf = new jsPDF({ unit: "pt", format: "letter" });
    const margin = 48;
    pdf.setFontSize(18);
    pdf.text("Background Check & Release Consent", margin, margin + 6);
    pdf.setFontSize(10);
    pdf.setTextColor(80);
    pdf.text(
      "Buller Taxi Services · Contractor Onboarding · Signed release form",
      margin, margin + 26,
    );

    const bodyLines = [
      "I, the undersigned, authorize Buller Taxi Services and its designated",
      "third-party providers to conduct a comprehensive background",
      "investigation, including but not limited to: motor vehicle records,",
      "criminal history, drug screening, employment verification, and",
      "credit-history where legally permitted.",
      "",
      "I certify that all information provided on this onboarding form is",
      "true and complete to the best of my knowledge. I understand that",
      "any false statement or omission may disqualify me from further",
      "consideration or result in termination if discovered post-hire.",
      "",
      "I acknowledge that this consent remains in effect throughout my",
      "engagement with Buller Taxi Services.",
    ];
    let y = margin + 60;
    pdf.setTextColor(20);
    pdf.setFontSize(11);
    bodyLines.forEach((ln) => {
      pdf.text(ln, margin, y);
      y += 16;
    });

    // Identity block
    y += 12;
    pdf.setFontSize(9);
    pdf.setTextColor(80);
    pdf.text(`Applicant: ${form.first_name} ${form.last_name}`, margin, y); y += 14;
    pdf.text(`Email: ${form.email}`, margin, y); y += 14;
    pdf.text(`Phone: ${form.driver_phone}`, margin, y); y += 14;
    pdf.text(`Date of birth: ${form.date_of_birth}`, margin, y); y += 14;
    pdf.text(`Signed on: ${form.background_release_date}`, margin, y); y += 24;

    // Signature raster
    pdf.setFontSize(10);
    pdf.setTextColor(20);
    pdf.text("Applicant Signature:", margin, y);
    const sigData = sigRef.current?.toDataURL();
    if (sigData) {
      // Signature area: 400 x 100 pt.
      pdf.addImage(sigData, "PNG", margin, y + 8, 380, 90);
    }
    // Return as data URL.
    return pdf.output("dataurlstring");
  }, [form]);

  // ── Submit ────────────────────────────────────────────────────────
  const onSubmit = async (e) => {
    e.preventDefault();
    const eMap = validate();
    if (Object.keys(eMap).length > 0) {
      toast.error("Please fix the highlighted fields");
      // Scroll to the first error field.
      const firstKey = Object.keys(eMap)[0];
      const node = document.querySelector(`[data-field="${firstKey}"]`)
                || document.querySelector(`[data-testid="cs-${firstKey}"]`);
      if (node?.scrollIntoView) {
        node.scrollIntoView({ behavior: "smooth", block: "center" });
        node.querySelector?.("input, select, textarea, button")?.focus?.();
      }
      return;
    }
    setSubmitting(true);
    try {
      const sig_pdf = await compileSignaturePDF();
      const documents = [];
      for (const [slot, val] of Object.entries(docs)) {
        if (val) documents.push({ slot, ...val });
      }
      const payload = {
        email:                form.email.trim().toLowerCase(),
        first_name:           form.first_name.trim(),
        middle_name:          form.middle_name.trim() || null,
        last_name:            form.last_name.trim(),
        driver_phone:         form.driver_phone.trim(),
        date_of_birth:        form.date_of_birth,
        ssn:                  form.ssn_digits,
        dl_number:            form.dl_number.trim(),
        dl_expiration_date:   form.dl_expiration_date,
        dl_issuing_state:     form.dl_issuing_state,
        address:              form.address.trim(),
        city:                 form.city.trim(),
        state:                form.state,
        zip:                  form.zip.trim(),
        documents,
        professional_references: refs.map((r) => ({
          name: r.name.trim(),
          phone: r.phone.trim(),
          email: r.email.trim().toLowerCase(),
          relationship: r.relationship.trim(),
        })),
        emergency_contact: {
          name: form.ec_name.trim(),
          phone: form.ec_phone.trim(),
          relationship: form.ec_relationship.trim(),
        },
        availability: {
          days_available: form.days_available,
          start_time:     form.start_time,
          end_time:       form.end_time,
        },
        banking: {
          bank_name:       form.bank_name.trim(),
          routing_number:  form.routing_number.replace(/\D/g, ""),
          account_number:  form.account_number.replace(/\D/g, ""),
          account_type:    form.account_type,
        },
        w9: {
          legal_name:         form.w9_legal_name.trim(),
          business_name:      form.w9_business_name.trim() || null,
          tax_classification: form.w9_tax_classification,
          tin_type:           form.w9_tin_type,
          tin_number:         form.w9_tin_number.replace(/\D/g, ""),
        },
        background_release_agreed: form.background_release_agreed,
        background_release_date:   form.background_release_date,
        signature_pdf_data_url:    sig_pdf,
      };
      await axios.post(`${API}/contractor-setup/submit`, payload);
      setDone(true);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      const raw = err?.response?.data?.detail;
      const msg = typeof raw === "string" ? raw : Array.isArray(raw)
        ? raw.map((r) => `${r.loc?.slice(-1)[0]}: ${r.msg}`).join(" · ")
        : "Submission failed";
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const hasErr = (k) => !!errors[k];
  const inputCls = (k) =>
    `w-full bg-zinc-900 border rounded px-3 py-2.5 text-sm text-zinc-100 focus:outline-none transition ${
      hasErr(k) ? "border-rose-500 focus:border-rose-400" : "border-zinc-700 focus:border-amber-400"
    }`;

  const errorCount = useMemo(() => Object.keys(errors).length, [errors]);

  if (done) {
    return (
      <div className="min-h-screen bg-[#0c0d10] text-zinc-100 flex items-center justify-center px-5">
        <div className="max-w-xl text-center space-y-4" data-testid="cs-success">
          <ShieldCheck className="h-14 w-14 text-emerald-300 mx-auto"/>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Onboarding packet received</h1>
          <p className="text-zinc-400 leading-relaxed">
            Thank you — a hiring manager will contact you within 1–2 business days.
            For urgent questions call <a href="tel:5802194687" className="text-amber-300 hover:text-amber-200 font-bold">(580) 219-4687</a>.
          </p>
          <Link to="/" className="inline-flex items-center gap-2 mt-4 text-amber-400 hover:text-amber-300 font-bold uppercase tracking-widest text-xs" data-testid="cs-success-home">
            <ArrowLeft className="h-4 w-4"/> Return to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0c0d10] text-zinc-100 py-8 px-4 sm:px-6" data-testid="contractor-setup-page">
      <div ref={topAnchorRef}/>
      {submitting && (
        <div className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-sm flex items-center justify-center" data-testid="cs-submit-overlay">
          <div className="bg-zinc-900 border border-zinc-700 rounded-lg px-6 py-5 flex items-center gap-3">
            <Loader2 className="h-5 w-5 animate-spin text-amber-400"/>
            <span className="text-sm font-bold">Submitting your onboarding packet…</span>
          </div>
        </div>
      )}

      <div className="max-w-4xl mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/40 text-amber-300 text-[10px] uppercase tracking-widest font-black">
            <Lock className="h-3 w-3"/> Private · Invite Only
          </div>
          <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Contractor Final Setup</h1>
          <p className="text-xs sm:text-sm text-zinc-500 max-w-xl mx-auto">
            All fields are transmitted over TLS and stored encrypted at rest. Complete the form
            in one sitting — you cannot save partial progress.
          </p>
        </div>

        {errorCount > 0 && (
          <div className="border border-rose-500 bg-rose-950/30 text-rose-200 rounded p-3 text-xs flex items-start gap-2" data-testid="cs-error-banner">
            <Info className="h-4 w-4 mt-0.5 shrink-0"/>
            <div>
              <div className="font-bold">Please fix {errorCount} field{errorCount > 1 ? "s" : ""}:</div>
              <div className="text-[11px] mt-0.5 opacity-90">
                {Object.values(errors).slice(0, 4).join(" · ")}
                {errorCount > 4 && ` · +${errorCount - 4} more`}
              </div>
            </div>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-8" noValidate data-testid="cs-form">
          {/* SECTION 1 — Identity */}
          <Section title="1 · Applicant Identity">
            <Row3>
              <F label="First Name" required error={errors.first_name} testid="cs-first_name">
                <input type="text" value={form.first_name} onChange={set("first_name")} className={inputCls("first_name")}/>
              </F>
              <F label="Middle Name" testid="cs-middle_name">
                <input type="text" value={form.middle_name} onChange={set("middle_name")} className={inputCls("middle_name")}/>
              </F>
              <F label="Last Name" required error={errors.last_name} testid="cs-last_name">
                <input type="text" value={form.last_name} onChange={set("last_name")} className={inputCls("last_name")}/>
              </F>
            </Row3>
            <Row3>
              <F label="Email" required error={errors.email} testid="cs-email">
                <input type="email" value={form.email} onChange={set("email")} className={inputCls("email")}/>
              </F>
              <F label="Cell Phone" required error={errors.driver_phone} testid="cs-driver_phone">
                <input type="tel" value={form.driver_phone} onChange={set("driver_phone")} className={inputCls("driver_phone")} placeholder="+15805551234"/>
              </F>
              <F label="Date of Birth" required error={errors.date_of_birth} testid="cs-date_of_birth">
                <input type="date" value={form.date_of_birth} onChange={set("date_of_birth")} className={inputCls("date_of_birth")}/>
              </F>
            </Row3>
            <F label="Social Security Number" required error={errors.ssn} testid="cs-ssn">
              <input
                type="text"
                inputMode="numeric"
                value={maskSSN(form.ssn_digits)}
                onChange={setSsn}
                autoComplete="off"
                spellCheck={false}
                placeholder="•••-••-••••"
                className={inputCls("ssn") + " font-mono tracking-widest"}
                aria-label="Social Security Number"
              />
              <p className="text-[10px] text-zinc-500 mt-1">Digits only. Encrypted before storage.</p>
            </F>
          </Section>

          {/* SECTION 2 — Driver's License */}
          <Section title="2 · Driver's License">
            <Row3>
              <F label="DL Number" required error={errors.dl_number} testid="cs-dl_number">
                <input type="text" value={form.dl_number} onChange={set("dl_number")} className={inputCls("dl_number")}/>
              </F>
              <F label="Expiration Date" required error={errors.dl_expiration_date} testid="cs-dl_expiration_date">
                <input type="date" value={form.dl_expiration_date} onChange={set("dl_expiration_date")} className={inputCls("dl_expiration_date")}/>
              </F>
              <F label="Issuing State" required error={errors.dl_issuing_state} testid="cs-dl_issuing_state">
                <select value={form.dl_issuing_state} onChange={set("dl_issuing_state")} className={inputCls("dl_issuing_state")}>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </F>
            </Row3>
          </Section>

          {/* SECTION 3 — Address */}
          <Section title="3 · Current Address">
            <F label="Street Address" required error={errors.address} testid="cs-address">
              <input type="text" value={form.address} onChange={set("address")} className={inputCls("address")}/>
            </F>
            <Row3>
              <F label="City" required error={errors.city} testid="cs-city">
                <input type="text" value={form.city} onChange={set("city")} className={inputCls("city")}/>
              </F>
              <F label="State" required error={errors.state} testid="cs-state">
                <select value={form.state} onChange={set("state")} className={inputCls("state")}>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </F>
              <F label="ZIP" required error={errors.zip} testid="cs-zip">
                <input type="text" value={form.zip} onChange={set("zip")} className={inputCls("zip")} inputMode="numeric"/>
              </F>
            </Row3>
          </Section>

          {/* SECTION 4 — Documents */}
          <Section title="4 · Identity Documents" desc="Photos are auto-cropped, contrast-enhanced, and re-encoded to JPEG on your device before upload.">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <DocumentSlot
                label="Front of Driver's License *"
                slot="dl_front"
                onChange={(v) => setDocs((d) => ({ ...d, dl_front: v }))}
                errorText={errors.dl_front || ""}
                data-testid="cs-doc-dl_front"
              />
              <DocumentSlot
                label="Back of Driver's License *"
                slot="dl_back"
                onChange={(v) => setDocs((d) => ({ ...d, dl_back: v }))}
                errorText={errors.dl_back || ""}
                data-testid="cs-doc-dl_back"
              />
              <DocumentSlot
                label="Proof of Motor Vehicle Insurance *"
                slot="insurance"
                onChange={(v) => setDocs((d) => ({ ...d, insurance: v }))}
                errorText={errors.insurance || ""}
                data-testid="cs-doc-insurance"
              />
              <DocumentSlot
                label="Additional Supporting Document"
                slot="slot4"
                slot4Options={SLOT4_TYPES}
                onChange={(v) => setDocs((d) => ({ ...d, slot4: v }))}
                errorText={errors.slot4 || ""}
                data-testid="cs-doc-slot4"
              />
            </div>
          </Section>

          {/* SECTION 5 — Professional References */}
          <Section title="5 · Professional References" desc="Three professional references. Family, relatives, and kin are not accepted.">
            {errors.refs_unique && (
              <div className="text-[11px] text-rose-400" data-field="refs_unique">{errors.refs_unique}</div>
            )}
            <div className="space-y-3">
              {refs.map((r, i) => (
                <div key={i} className="border border-zinc-800 rounded p-3 space-y-2" data-testid={`cs-ref-${i}`}>
                  <div className="text-[10px] uppercase tracking-widest text-amber-400 font-black">Reference #{i + 1}</div>
                  <Row2>
                    <F label="Full Name" required error={errors[`ref_${i}_name`]} testid={`cs-ref_${i}_name`}>
                      <input type="text" value={r.name} onChange={setRef(i, "name")} className={inputCls(`ref_${i}_name`)}/>
                    </F>
                    <F label="Relationship" required error={errors[`ref_${i}_rel`]} testid={`cs-ref_${i}_rel`}>
                      <input type="text" value={r.relationship} onChange={setRef(i, "relationship")} className={inputCls(`ref_${i}_rel`)} placeholder="e.g. Former supervisor"/>
                    </F>
                  </Row2>
                  <Row2>
                    <F label="Phone (E.164)" required error={errors[`ref_${i}_phone`]} testid={`cs-ref_${i}_phone`}>
                      <input type="tel" value={r.phone} onChange={setRef(i, "phone")} className={inputCls(`ref_${i}_phone`)} placeholder="+15805551234"/>
                    </F>
                    <F label="Email" required error={errors[`ref_${i}_email`]} testid={`cs-ref_${i}_email`}>
                      <input type="email" value={r.email} onChange={setRef(i, "email")} className={inputCls(`ref_${i}_email`)}/>
                    </F>
                  </Row2>
                </div>
              ))}
            </div>
          </Section>

          {/* SECTION 6 — Emergency Contact */}
          <Section title="6 · Emergency Contact" desc="Cannot be the same phone number as your own cell.">
            <Row3>
              <F label="Full Name" required error={errors.ec_name} testid="cs-ec_name">
                <input type="text" value={form.ec_name} onChange={set("ec_name")} className={inputCls("ec_name")}/>
              </F>
              <F label="Phone" required error={errors.ec_phone} testid="cs-ec_phone">
                <input type="tel" value={form.ec_phone} onChange={set("ec_phone")} className={inputCls("ec_phone")}/>
              </F>
              <F label="Relationship" required error={errors.ec_relationship} testid="cs-ec_relationship">
                <input type="text" value={form.ec_relationship} onChange={set("ec_relationship")} className={inputCls("ec_relationship")}/>
              </F>
            </Row3>
          </Section>

          {/* SECTION 7 — Availability */}
          <Section title="7 · Availability">
            <div data-field="days_available" data-testid="cs-days_available">
              <label className="block text-[11px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">Days available *</label>
              <div className="flex flex-wrap gap-1.5">
                {DAYS.map((d) => {
                  const active = form.days_available.includes(d);
                  return (
                    <button
                      key={d}
                      type="button"
                      onClick={() => toggleDay(d)}
                      data-testid={`cs-day-${d}`}
                      className={`text-xs font-black uppercase tracking-widest px-3 py-1.5 rounded-full border transition ${
                        active
                          ? "bg-amber-400 text-zinc-950 border-amber-400"
                          : "bg-zinc-900 text-zinc-300 border-zinc-700 hover:border-amber-400/60"
                      }`}
                    >
                      {d}
                    </button>
                  );
                })}
              </div>
              {errors.days_available && <p className="text-[11px] text-rose-400 mt-1">{errors.days_available}</p>}
            </div>
            <Row2>
              <F label="Start Time" required testid="cs-start_time">
                <input type="time" value={form.start_time} onChange={set("start_time")} className={inputCls("start_time")}/>
              </F>
              <F label="End Time" required error={errors.end_time} testid="cs-end_time">
                <input type="time" value={form.end_time} onChange={set("end_time")} className={inputCls("end_time")}/>
              </F>
            </Row2>
          </Section>

          {/* SECTION 8 — Banking */}
          <Section title="8 · Direct Deposit (ACH)" desc="Required for weekly payouts. Encrypted at rest — only last-4 shown to admins.">
            <Row2>
              <F label="Bank Name" required error={errors.bank_name} testid="cs-bank_name">
                <input type="text" value={form.bank_name} onChange={set("bank_name")} className={inputCls("bank_name")}/>
              </F>
              <F label="Account Type" required testid="cs-account_type">
                <select value={form.account_type} onChange={set("account_type")} className={inputCls("account_type")}>
                  <option value="checking">Checking</option>
                  <option value="savings">Savings</option>
                </select>
              </F>
            </Row2>
            <Row2>
              <F label="Routing Number (9 digits)" required error={errors.routing_number} testid="cs-routing_number">
                <input type="text" value={form.routing_number} onChange={set("routing_number")} className={inputCls("routing_number") + " font-mono"} inputMode="numeric" maxLength={9} autoComplete="off"/>
              </F>
              <F label="Account Number" required error={errors.account_number} testid="cs-account_number">
                <input type="text" value={form.account_number} onChange={set("account_number")} className={inputCls("account_number") + " font-mono"} inputMode="numeric" autoComplete="off"/>
              </F>
            </Row2>
          </Section>

          {/* SECTION 9 — W-9 / Tax */}
          <Section title="9 · W-9 Tax Information">
            <Row2>
              <F label="Legal Name (as on tax return)" required error={errors.w9_legal_name} testid="cs-w9_legal_name">
                <input type="text" value={form.w9_legal_name} onChange={set("w9_legal_name")} className={inputCls("w9_legal_name")}/>
              </F>
              <F label="Business Name (optional)" testid="cs-w9_business_name">
                <input type="text" value={form.w9_business_name} onChange={set("w9_business_name")} className={inputCls("w9_business_name")}/>
              </F>
            </Row2>
            <Row3>
              <F label="Federal Tax Classification" required testid="cs-w9_tax_classification">
                <select value={form.w9_tax_classification} onChange={set("w9_tax_classification")} className={inputCls("w9_tax_classification")}>
                  <option value="individual">Individual / Sole proprietor</option>
                  <option value="sole_proprietor">Sole proprietor (DBA)</option>
                  <option value="llc">LLC</option>
                  <option value="c_corp">C Corporation</option>
                  <option value="s_corp">S Corporation</option>
                  <option value="partnership">Partnership</option>
                  <option value="trust_estate">Trust / Estate</option>
                  <option value="other">Other</option>
                </select>
              </F>
              <F label="TIN Type" required testid="cs-w9_tin_type">
                <select value={form.w9_tin_type} onChange={set("w9_tin_type")} className={inputCls("w9_tin_type")}>
                  <option value="ssn">Social Security Number (SSN)</option>
                  <option value="ein">Employer ID Number (EIN)</option>
                </select>
              </F>
              <F label="TIN Number" required error={errors.w9_tin_number} testid="cs-w9_tin_number">
                <input
                  type="text"
                  value={form.w9_tin_number}
                  onChange={(e) => setForm((f) => ({ ...f, w9_tin_number: e.target.value.replace(/\D/g, "").slice(0, 15) }))}
                  className={inputCls("w9_tin_number") + " font-mono tracking-widest"}
                  inputMode="numeric"
                  autoComplete="off"
                />
              </F>
            </Row3>
          </Section>

          {/* SECTION 10 — Background Release + Signature */}
          <Section title="10 · Background Check Consent + Signature">
            <label className="flex items-start gap-2 cursor-pointer" data-field="background_release_agreed" data-testid="cs-background_release_agreed">
              <input
                type="checkbox"
                checked={form.background_release_agreed}
                onChange={set("background_release_agreed")}
                className="mt-1 h-4 w-4 accent-amber-400"
              />
              <span className="text-xs text-zinc-300 leading-relaxed">
                I authorize Buller Taxi Services and its designated third-party providers to conduct a
                comprehensive background investigation — including motor vehicle records, criminal
                history, drug screening, employment verification, and credit history where legally
                permitted — and confirm the information on this form is true and complete to the best
                of my knowledge.
              </span>
            </label>
            {errors.background_release_agreed && (
              <p className="text-[11px] text-rose-400">{errors.background_release_agreed}</p>
            )}
            <div className="pt-2" data-field="signature" data-testid="cs-signature">
              <label className="block text-[11px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">
                <PenLine className="h-3 w-3 inline mr-1"/> Signature *
              </label>
              <SignatureCanvas ref={sigRef} data-testid="cs-signature-canvas"/>
              {errors.signature && <p className="text-[11px] text-rose-400 mt-1">{errors.signature}</p>}
            </div>
          </Section>

          {/* Submit */}
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-2 border-t border-zinc-800">
            <button
              type="submit"
              disabled={submitting}
              data-testid="cs-submit"
              className="inline-flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-300 disabled:opacity-50 text-zinc-950 font-black uppercase tracking-widest text-sm px-8 py-3 rounded transition"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin"/>}
              {submitting ? "Submitting…" : "Submit Onboarding Packet"}
            </button>
            <p className="text-[11px] text-zinc-500">
              By submitting, you consent to the background check and confirm all information is accurate.
            </p>
          </div>
        </form>

        <p className="text-[10px] text-center text-zinc-600 pt-4">
          Reached this page in error? <Link to="/" className="text-zinc-400 hover:text-amber-300">Return to home</Link>.
        </p>
      </div>
    </div>
  );
}

// ── Layout helpers ─────────────────────────────────────────────────────
function Section({ title, desc, children }) {
  return (
    <div className="bg-zinc-950 border border-zinc-800 rounded-lg p-5 sm:p-6 space-y-4">
      <div>
        <h2 className="text-lg font-black tracking-tight text-zinc-100">{title}</h2>
        {desc && <p className="text-xs text-zinc-500 mt-1">{desc}</p>}
      </div>
      {children}
    </div>
  );
}
function Row2({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-2 gap-4">{children}</div>;
}
function Row3({ children }) {
  return <div className="grid grid-cols-1 md:grid-cols-3 gap-4">{children}</div>;
}
function F({ label, required, error, children, testid }) {
  return (
    <label className="block" data-field={testid?.replace(/^cs-/, "") || ""} data-testid={testid}>
      <span className="block text-[11px] uppercase tracking-widest text-zinc-400 font-bold mb-1.5">
        {label} {required && <span className="text-rose-400">*</span>}
      </span>
      {children}
      {error && <span className="block text-[11px] text-rose-400 mt-1">{error}</span>}
    </label>
  );
}
